/**
 * Planned Expenses — financial timing & control centre.
 *
 * Layout (top → bottom):
 *   1. Cash Pressure / Due-Soon signal
 *   2. Financial Calendar (grid + timeline)
 *   3. Plan Entry Strip (email / upload / add)
 *   4. Summary Strip (income / expenses / remaining)
 *   5. Unified planned-expense list by bucket
 */
import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  Collapsible, CollapsibleContent, CollapsibleTrigger,
} from "../components/ui/collapsible";
import { toast } from "sonner";
import {
  Plus, MoreHorizontal, Loader2, ChevronDown, ChevronRight,
  Edit, Trash2, Pause, Play, Home, Receipt, PiggyBank, Sparkles,
  FolderOpen, Wallet, CalendarDays, FileText, Mail, Upload, Copy, Check,
  ArrowDownLeft, ArrowUpRight, CreditCard, Banknote, RefreshCw, ExternalLink,
} from "lucide-react";
import { ConversationPrompt } from "../components/privacy/ConversationPrompt";

const API = process.env.REACT_APP_BACKEND_URL;

/* ── tiny helpers ── */
const GROUP_ICONS = { home: Home, receipt: Receipt, "piggy-bank": PiggyBank, sparkles: Sparkles, folder: FolderOpen };
const CYCLE_LABELS = { weekly: "/ week", fortnightly: "/ fortnight", monthly: "/ month", per_pay: "/ pay" };

function fmt$(amt, cur = "NZD") {
  return new Intl.NumberFormat("en-NZ", { style: "currency", currency: cur, minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(amt || 0);
}
function fmtCycle(amt, cycle, cur) {
  return `${fmt$(amt, cur)} ${CYCLE_LABELS[cycle] || "/ week"}`;
}
function relTime(d) {
  if (!d) return "";
  const ms = Date.now() - new Date(d).getTime();
  const m = Math.floor(ms / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const dy = Math.floor(h / 24);
  if (dy < 7) return `${dy}d ago`;
  return new Date(d).toLocaleDateString("en-NZ", { month: "short", day: "numeric" });
}
function dateKey(d) { return d.toISOString().slice(0, 10); }

const BILL_STATUS = {
  upcoming: { label: "Upcoming", bg: "bg-[#1a4d3e]/[0.06]", text: "text-[#1a4d3e]/70" },
  due_soon: { label: "Due soon", bg: "bg-[#b8860b]/[0.08]", text: "text-[#8b6914]" },
  overdue: { label: "Overdue", bg: "bg-[#8b4513]/[0.08]", text: "text-[#8b4513]" },
  paid: { label: "Paid", bg: "bg-[#1a6b3e]/[0.08]", text: "text-[#1a6b3e]" },
  needs_review: { label: "Needs review", bg: "bg-[#5a5a3d]/[0.06]", text: "text-[#5a5a3d]" },
  active: { label: "Active", bg: "bg-[#1a4d3e]/[0.06]", text: "text-[#1a4d3e]/70" },
};
const TYPE_COLORS = { income: "#1a6b3e", bill: "#8b6914", expense: "#6b5a3d", debt: "#9f1239" };
const TYPE_ICONS = { income: Banknote, bill: FileText, debt: CreditCard, expense: Receipt };
const DAY_HDRS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

/* ================================================================
   CALENDAR SYNC MODAL
   ================================================================ */
function CalendarSyncModal({ open, onOpenChange, household }) {
  const [icsUrl, setIcsUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [synced, setSynced] = useState(false);

  useEffect(() => {
    if (!open || !household?.household_id) return;
    setLoading(true);
    setCopied(false);
    setSynced(false);
    fetch(`${API}/api/households/${household.household_id}/calendar/financial/subscribe`, { method: "POST", credentials: "include" })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.url) setIcsUrl(`${API}${d.url}`); })
      .catch(() => toast.error("Could not generate calendar link"))
      .finally(() => setLoading(false));
  }, [open, household?.household_id]);

  const copyUrl = async () => {
    if (!icsUrl) return;
    try { await navigator.clipboard.writeText(icsUrl); setCopied(true); setSynced(true); setTimeout(() => setCopied(false), 2500); }
    catch { toast.error("Couldn't copy"); }
  };

  const webcalUrl = icsUrl ? icsUrl.replace(/^https?:\/\//, "webcal://") : "";
  const encodedUrl = icsUrl ? encodeURIComponent(icsUrl) : "";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" aria-describedby="cal-sync-desc">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <CalendarDays className="w-5 h-5 text-[#1a4d3e]" />Add your financial calendar
          </DialogTitle>
        </DialogHeader>
        <p id="cal-sync-desc" className="text-sm text-muted-foreground/70 -mt-2">
          See your upcoming income and payments alongside your day-to-day calendar.
        </p>

        {loading ? (
          <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
        ) : icsUrl ? (
          <div className="space-y-4 py-2">
            {/* ICS URL + Copy */}
            <div>
              <Label className="text-xs text-muted-foreground mb-1.5 block">Your private calendar link</Label>
              <div className="flex items-center gap-2">
                <div className="flex-1 bg-slate-50 dark:bg-slate-800 rounded-lg px-3 py-2 border border-slate-200 dark:border-slate-700 overflow-hidden">
                  <p className="text-xs font-mono text-slate-600 dark:text-slate-300 truncate" data-testid="ics-url-display">{icsUrl}</p>
                </div>
                <Button size="sm" variant="outline" className="rounded-full h-8 px-3 flex-shrink-0" onClick={copyUrl} data-testid="copy-ics-url-btn">
                  {copied ? <><Check className="w-3.5 h-3.5 text-emerald-500 mr-1" />Copied</> : <><Copy className="w-3.5 h-3.5 mr-1" />Copy</>}
                </Button>
              </div>
            </div>

            {/* Quick-open buttons */}
            <div>
              <Label className="text-xs text-muted-foreground mb-2 block">Or add directly</Label>
              <div className="grid grid-cols-1 gap-2">
                <a
                  href={`https://calendar.google.com/calendar/r?cid=${encodeURIComponent(webcalUrl)}`}
                  target="_blank" rel="noopener noreferrer"
                  onClick={() => setSynced(true)}
                  className="flex items-center justify-between px-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
                  data-testid="google-cal-btn"
                >
                  <span className="text-sm font-medium text-slate-700 dark:text-slate-200">Google Calendar</span>
                  <ExternalLink className="w-3.5 h-3.5 text-slate-400" />
                </a>
                <a
                  href={`https://outlook.live.com/calendar/0/addfromweb?url=${encodedUrl}&name=ARA%20Wealth`}
                  target="_blank" rel="noopener noreferrer"
                  onClick={() => setSynced(true)}
                  className="flex items-center justify-between px-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
                  data-testid="outlook-cal-btn"
                >
                  <span className="text-sm font-medium text-slate-700 dark:text-slate-200">Outlook</span>
                  <ExternalLink className="w-3.5 h-3.5 text-slate-400" />
                </a>
                <a
                  href={webcalUrl}
                  onClick={() => setSynced(true)}
                  className="flex items-center justify-between px-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
                  data-testid="apple-cal-btn"
                >
                  <span className="text-sm font-medium text-slate-700 dark:text-slate-200">Apple Calendar</span>
                  <ExternalLink className="w-3.5 h-3.5 text-slate-400" />
                </a>
              </div>
            </div>

            {/* Confirmation */}
            {synced && (
              <div className="bg-[#1a4d3e]/[0.04] rounded-xl px-4 py-3 flex items-center gap-2" data-testid="sync-confirmation">
                <RefreshCw className="w-4 h-4 text-[#1a4d3e]/60" />
                <p className="text-sm text-[#1a4d3e]/80">Your calendar will stay in sync automatically</p>
              </div>
            )}
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

/* ================================================================
   1. FINANCIAL CALENDAR  — grid + upcoming timeline
   ================================================================ */
function FinancialCalendar({ events, currency, onSync }) {
  const [selectedDate, setSelectedDate] = useState(null);

  /* build 14-day grid starting from Monday of current week */
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const todayStr = dateKey(today);
  const dow = today.getDay(); // 0=Sun
  const monOff = dow === 0 ? -6 : 1 - dow;
  const weekStart = new Date(today); weekStart.setDate(today.getDate() + monOff);
  const calDays = Array.from({ length: 14 }, (_, i) => {
    const d = new Date(weekStart); d.setDate(weekStart.getDate() + i);
    return { date: d, key: dateKey(d), isPast: d < today, isToday: dateKey(d) === todayStr };
  });

  /* index events by date */
  const byDate = {};
  (events || []).forEach(e => { const k = e.date; (byDate[k] ||= []).push(e); });

  /* timeline: selected-day filter or next upcoming */
  const timeline = selectedDate
    ? (byDate[selectedDate] || [])
    : (events || []).filter(e => e.date >= todayStr).slice(0, 12);

  /* month labels for each row */
  const row1Month = calDays[0].date.toLocaleDateString("en-NZ", { month: "short" });
  const row2Month = calDays[7].date.toLocaleDateString("en-NZ", { month: "short" });
  const row2MonthStr = row1Month !== row2Month ? row2Month : null;

  return (
    <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-sm overflow-hidden" data-testid="financial-calendar">
      {/* ── Mini Grid ── */}
      <div className="px-5 pt-5 pb-3">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">What's coming up</h3>
          <div className="flex items-center gap-2">
            <button onClick={onSync} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium text-slate-500 hover:text-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors" data-testid="calendar-sync-btn">
              <RefreshCw className="w-3 h-3" />Sync
            </button>
            <span className="text-[11px] text-muted-foreground/50">
              {calDays[0].date.toLocaleDateString("en-NZ", { month: "short", day: "numeric" })} – {calDays[13].date.toLocaleDateString("en-NZ", { month: "short", day: "numeric" })}
            </span>
          </div>
        </div>

        {/* Day headers */}
        <div className="grid grid-cols-[repeat(7,1fr)_28px] gap-1 mb-1">
          {DAY_HDRS.map(h => (
            <div key={h} className="text-center text-[10px] text-muted-foreground/40 font-medium">{h}</div>
          ))}
          <div />
        </div>

        {/* Row 1 */}
        <div className="grid grid-cols-[repeat(7,1fr)_28px] gap-1 mb-1">
          {calDays.slice(0, 7).map(d => (
            <CalDay key={d.key} d={d} events={byDate[d.key]} selected={selectedDate === d.key} onSelect={() => setSelectedDate(selectedDate === d.key ? null : d.key)} />
          ))}
          <span className="text-[9px] text-muted-foreground/30 flex items-center justify-center">{row1Month}</span>
        </div>

        {/* Row 2 */}
        <div className="grid grid-cols-[repeat(7,1fr)_28px] gap-1">
          {calDays.slice(7, 14).map(d => (
            <CalDay key={d.key} d={d} events={byDate[d.key]} selected={selectedDate === d.key} onSelect={() => setSelectedDate(selectedDate === d.key ? null : d.key)} />
          ))}
          <span className="text-[9px] text-muted-foreground/30 flex items-center justify-center">{row2MonthStr || ""}</span>
        </div>
      </div>

      {/* ── Upcoming Timeline ── */}
      <div className="border-t border-slate-100 dark:border-slate-800 px-5 py-3" data-testid="calendar-timeline">
        {timeline.length === 0 ? (
          <p className="text-xs text-muted-foreground/40 text-center py-4">
            {selectedDate ? "Nothing scheduled for this day" : "No upcoming events"}
          </p>
        ) : (
          <div className="space-y-1 max-h-[220px] overflow-y-auto pr-1">
            {timeline.map(evt => {
              const Icon = TYPE_ICONS[evt.type] || Receipt;
              const isIn = evt.direction === "incoming";
              return (
                <div key={evt.event_id} className="flex items-center gap-3 px-2 py-2 rounded-lg hover:bg-slate-50/60 dark:hover:bg-slate-800/30 transition-colors" data-testid={`cal-event-${evt.event_id}`}>
                  <div className="w-1 h-8 rounded-full flex-shrink-0" style={{ backgroundColor: evt.category_color || TYPE_COLORS[evt.type] }} />
                  <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${evt.category_color || TYPE_COLORS[evt.type]}12` }}>
                    <Icon className="w-3.5 h-3.5" style={{ color: evt.category_color || TYPE_COLORS[evt.type] }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900 dark:text-slate-100 truncate">{evt.title}</p>
                    <p className="text-[10px] text-muted-foreground/50">
                      {new Date(evt.date + "T00:00").toLocaleDateString("en-NZ", { weekday: "short", month: "short", day: "numeric" })}
                      {evt.frequency ? ` · ${evt.frequency}` : ""}
                    </p>
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    {isIn
                      ? <ArrowDownLeft className="w-3 h-3 text-[#1a6b3e]/60" />
                      : <ArrowUpRight className="w-3 h-3 text-slate-400" />}
                    <p className={`text-sm font-semibold tabular-nums ${isIn ? "text-[#1a6b3e]" : "text-slate-900 dark:text-slate-100"}`}>
                      {isIn ? "+" : ""}{fmt$(evt.amount, currency)}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

/* single calendar-grid day cell */
function CalDay({ d, events, selected, onSelect }) {
  const types = [...new Set((events || []).map(e => e.type))];
  const hasEvents = types.length > 0;
  return (
    <button
      onClick={onSelect}
      className={[
        "relative flex flex-col items-center py-1.5 rounded-lg transition-all text-center",
        selected ? "bg-slate-900 dark:bg-slate-100 ring-1 ring-slate-900/20 dark:ring-slate-100/20" :
        d.isToday ? "bg-slate-100 dark:bg-slate-800" :
        d.isPast ? "opacity-40" :
        "hover:bg-slate-50 dark:hover:bg-slate-800/50",
      ].join(" ")}
      data-testid={`cal-day-${d.key}`}
    >
      <span className={`text-xs font-medium leading-none ${selected ? "text-white dark:text-slate-900" : d.isToday ? "font-bold text-slate-900 dark:text-slate-100" : "text-slate-600 dark:text-slate-400"}`}>
        {d.date.getDate()}
      </span>
      {hasEvents && (
        <div className="flex gap-[3px] mt-1">
          {types.slice(0, 3).map(t => (
            <span key={t} className="w-[5px] h-[5px] rounded-full" style={{ backgroundColor: selected ? "#fff" : (TYPE_COLORS[t] || "#999") }} />
          ))}
        </div>
      )}
    </button>
  );
}

/* ================================================================
   2. CASH PRESSURE SIGNAL
   ================================================================ */
function CashPressureCard({ signal }) {
  const cfg = {
    pressure: { bg: "bg-[#8b4513]/[0.04]", line: "This cycle might feel a bit tight", sub: "Upcoming payments are higher than what's currently available" },
    due_soon: { bg: "bg-[#6b5a3d]/[0.04]", line: signal.count === 1 ? "Something's coming up soon" : "A few things are coming up soon", sub: `${signal.count} payment${signal.count !== 1 ? "s" : ""} due in the next few days` },
    clear: { bg: "bg-[#1a4d3e]/[0.04]", line: "You've got space this cycle", sub: null },
  }[signal.type];
  return (
    <div className={`rounded-xl px-5 py-3.5 ${cfg.bg} transition-all duration-500`} data-testid="cash-pressure-signal" data-signal={signal.type}>
      <p className="text-sm font-medium text-foreground/80" data-testid="pressure-signal-line">{cfg.line}</p>
      {cfg.sub && <p className="text-xs text-muted-foreground/60 mt-0.5">{cfg.sub}</p>}
    </div>
  );
}

/* ================================================================
   3. PLAN ENTRY STRIP
   ================================================================ */
function PlanEntryStrip({ household, onAddBill }) {
  const [inbox, setInbox] = useState(null);
  const [lastImport, setLastImport] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!household?.household_id) return;
    fetch(`${API}/api/households/${household.household_id}/bills/inbox`, { credentials: "include" })
      .then(r => r.ok ? r.json() : null).then(d => { if (d) setInbox(d); }).catch(() => {});
    fetch(`${API}/api/households/${household.household_id}/bills/inbox/imports?limit=1`, { credentials: "include" })
      .then(r => r.ok ? r.json() : null).then(d => { if (d?.imports?.length) setLastImport(d.imports[0]); }).catch(() => {});
  }, [household?.household_id]);

  const copyEmail = async () => {
    if (!inbox?.email_alias) return;
    try { await navigator.clipboard.writeText(inbox.email_alias); setCopied(true); setTimeout(() => setCopied(false), 2000); }
    catch { toast.error("Couldn't copy — select the address manually"); }
  };

  return (
    <div className="bg-white dark:bg-slate-900 rounded-2xl px-5 py-3.5 shadow-sm" data-testid="plan-entry-strip">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <p className="text-xs font-medium text-slate-400 uppercase tracking-wide">Add to your plan</p>
        <div className="flex items-center gap-2 flex-wrap">
          {inbox?.enabled && inbox?.email_alias && (
            <div className="flex items-center gap-2">
              <button onClick={copyEmail} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors text-xs" data-testid="copy-email-btn">
                <Mail className="w-3 h-3 text-slate-400" />
                <span className="text-slate-600 dark:text-slate-300 font-mono text-[11px] max-w-[200px] truncate">{inbox.email_alias}</span>
                {copied ? <Check className="w-3 h-3 text-emerald-500" /> : <Copy className="w-3 h-3 text-slate-400" />}
              </button>
              <span className="text-[10px] text-muted-foreground/50 hidden sm:inline" data-testid="last-received-indicator">
                {lastImport ? `Last received: ${relTime(lastImport.received_at)}` : "No bills received yet"}
              </span>
            </div>
          )}
          <Button size="sm" variant="outline" className="rounded-full text-xs h-7" onClick={() => toast("Bill upload coming soon — forward bills to your inbox address instead", { duration: 4000 })} data-testid="upload-bill-btn">
            <Upload className="w-3 h-3 mr-1" />Upload bill
          </Button>
          <Button size="sm" variant="outline" className="rounded-full text-xs h-7" onClick={onAddBill} data-testid="add-bill-entry-btn">
            <Plus className="w-3 h-3 mr-1" />Add bill
          </Button>
        </div>
      </div>
    </div>
  );
}

/* ================================================================
   4. EXPENSE ROW  (unified — manual + bill-sourced)
   ================================================================ */
function ExpenseRow({ alloc, currency, onEdit, onDelete, onPause }) {
  const isPaused = alloc.status === "paused";
  const isBill = !!alloc.bill_id;
  const bill = alloc._bill;
  const sCfg = bill?.display_status ? BILL_STATUS[bill.display_status] || BILL_STATUS.upcoming : null;
  const due = bill?.next_due ? new Date(bill.next_due) : null;

  return (
    <div className={`group flex items-center justify-between p-4 rounded-xl transition-colors ${isPaused ? "opacity-50" : "hover:bg-slate-50/60 dark:hover:bg-slate-800/40"}`} data-testid={`expense-row-${alloc.allocation_id}`}>
      <div className="flex-1 min-w-0 mr-3">
        <div className="flex items-center gap-2 flex-wrap">
          <p className={`font-medium ${isPaused ? "text-slate-500" : "text-slate-900 dark:text-slate-100"}`}>{alloc.name}</p>
          {isBill ? (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-[#1a4d3e]/[0.06] text-[#1a4d3e]/70" data-testid={`source-bill-${alloc.allocation_id}`}><FileText className="w-2.5 h-2.5" />Bill</span>
          ) : (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-slate-100 dark:bg-slate-800 text-slate-500" data-testid={`source-manual-${alloc.allocation_id}`}><Edit className="w-2.5 h-2.5" />Manual</span>
          )}
          {isBill && sCfg && <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${sCfg.bg} ${sCfg.text}`} data-testid={`bill-status-${alloc.allocation_id}`}>{sCfg.label}</span>}
          {isPaused && <span className="px-2 py-0.5 rounded-full text-xs bg-slate-200 dark:bg-slate-700 text-slate-500">Paused</span>}
          {alloc.behavior === "temporary" && <span className="px-2 py-0.5 rounded-full text-xs bg-amber-100 dark:bg-amber-900/30 text-amber-700">Temporary</span>}
        </div>
        <div className="flex items-center gap-3 mt-0.5 flex-wrap">
          {alloc.category_name && <p className="text-xs text-slate-400">Category: {alloc.category_name}</p>}
          {isBill && due && <p className="text-xs text-slate-400 flex items-center gap-1"><CalendarDays className="w-3 h-3" />Due {due.toLocaleDateString("en-NZ", { month: "short", day: "numeric" })}</p>}
          {isBill && bill?.frequency && <p className="text-xs text-slate-400 capitalize">{bill.frequency}</p>}
        </div>
      </div>
      <div className="flex items-center gap-3">
        <p className={`font-medium tabular-nums ${isPaused ? "text-slate-400" : "text-slate-900 dark:text-slate-100"}`}>{fmtCycle(alloc.amount, alloc.cycle, currency)}</p>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0 opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`expense-menu-${alloc.allocation_id}`}><MoreHorizontal className="w-4 h-4 text-slate-400" /></Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-44">
            <DropdownMenuItem onClick={() => onEdit(alloc)}><Edit className="w-4 h-4 mr-2" />Edit</DropdownMenuItem>
            <DropdownMenuItem onClick={() => onPause(alloc)}>{isPaused ? <><Play className="w-4 h-4 mr-2" />Resume</> : <><Pause className="w-4 h-4 mr-2" />Pause</>}</DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => onDelete(alloc)} className="text-red-600"><Trash2 className="w-4 h-4 mr-2" />Delete</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}

/* ================================================================
   5. BUCKET GROUP
   ================================================================ */
function BucketGroup({ group, allocations, currency, onAdd, onEdit, onDelete, onPause, collapsed, onToggle }) {
  const Icon = GROUP_ICONS[group.icon] || FolderOpen;
  const active = allocations.filter(a => a.status === "active");
  const total = active.reduce((s, a) => s + (a.amount || 0), 0);
  return (
    <Collapsible open={!collapsed} onOpenChange={onToggle}>
      <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-sm overflow-hidden" data-testid={`bucket-group-${group.group_id}`}>
        <CollapsibleTrigger asChild>
          <button className="w-full flex items-center justify-between p-5 hover:bg-slate-50/50 dark:hover:bg-slate-800/30 transition-colors">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${group.color}15` }}><Icon className="w-5 h-5" style={{ color: group.color }} /></div>
              <div className="text-left">
                <p className="font-medium text-slate-900 dark:text-slate-100">{group.name}</p>
                <p className="text-sm text-slate-500">{active.length} item{active.length !== 1 ? "s" : ""}</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <p className="font-heading text-lg font-semibold text-slate-900 dark:text-slate-100 tabular-nums">{fmt$(total, currency)}</p>
              {collapsed ? <ChevronRight className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
            </div>
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="px-5 pb-5">
            {allocations.length === 0 ? (
              <div className="text-center py-8 border-2 border-dashed border-slate-200 dark:border-slate-700 rounded-xl">
                <Wallet className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                <p className="text-sm text-muted-foreground mb-1">Nothing here yet</p>
                <p className="text-xs text-muted-foreground/60 mb-3">Add the essentials first, then build from there.</p>
                <Button onClick={() => onAdd(group)} variant="outline" size="sm" className="rounded-full"><Plus className="w-4 h-4 mr-2" />Add an expense</Button>
              </div>
            ) : (
              <div className="space-y-1">
                {allocations.map(a => <ExpenseRow key={a.allocation_id} alloc={a} currency={currency} onEdit={onEdit} onDelete={onDelete} onPause={onPause} />)}
                <Button onClick={() => onAdd(group)} variant="ghost" size="sm" className="w-full justify-start text-slate-500 hover:text-slate-700 mt-2"><Plus className="w-4 h-4 mr-2" />Add expense</Button>
              </div>
            )}
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
}

/* ================================================================
   6. EXPENSE DIALOG
   ================================================================ */
function ExpenseDialog({ open, onOpenChange, allocation, groups, categories, onSave, loading }) {
  const isEdit = !!allocation?.allocation_id;
  const [form, setForm] = useState({ group_id: "", name: "", amount: "", cycle: "weekly", behavior: "fixed", category_id: "", notes: "" });
  useEffect(() => {
    if (allocation?.allocation_id) {
      setForm({ group_id: allocation.group_id || "", name: allocation.name || "", amount: allocation.amount || "", cycle: allocation.cycle || "weekly", behavior: allocation.behavior || "fixed", category_id: allocation.category_id || "", notes: allocation.notes || "" });
    } else {
      setForm({ group_id: allocation?.group_id || (groups[0]?.group_id || ""), name: "", amount: "", cycle: "weekly", behavior: "fixed", category_id: "", notes: "" });
    }
  }, [allocation, open, groups]);
  const submit = () => {
    if (!form.name.trim()) { toast.error("Enter a name"); return; }
    if (!form.amount || parseFloat(form.amount) <= 0) { toast.error("Enter an amount"); return; }
    onSave({ ...form, amount: parseFloat(form.amount), category_id: form.category_id || null, allocation_id: allocation?.allocation_id });
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" aria-describedby="expense-dlg-desc">
        <DialogHeader><DialogTitle className="font-heading">{isEdit ? "Edit expense" : "Add an expense"}</DialogTitle></DialogHeader>
        <p id="expense-dlg-desc" className="sr-only">Planned expense form</p>
        <div className="space-y-4 py-4">
          <div><Label className="text-xs text-slate-500">Name</Label><Input placeholder="e.g., Groceries, Rent" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} data-testid="expense-name-input" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs text-slate-500">Amount</Label><Input type="number" min="0" placeholder="0" value={form.amount} onChange={e => setForm(p => ({ ...p, amount: e.target.value }))} data-testid="expense-amount-input" /></div>
            <div><Label className="text-xs text-slate-500">Frequency</Label>
              <Select value={form.cycle} onValueChange={v => setForm(p => ({ ...p, cycle: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="weekly">per week</SelectItem><SelectItem value="fortnightly">per fortnight</SelectItem><SelectItem value="monthly">per month</SelectItem><SelectItem value="per_pay">per pay</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
          <div><Label className="text-xs text-slate-500">Bucket</Label>
            <Select value={form.group_id} onValueChange={v => setForm(p => ({ ...p, group_id: v }))}>
              <SelectTrigger><SelectValue placeholder="Select bucket" /></SelectTrigger>
              <SelectContent>{groups.map(g => <SelectItem key={g.group_id} value={g.group_id}>{g.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs text-slate-500">Category <span className="text-slate-400">(for spending tracking)</span></Label>
            <Select value={form.category_id || "none"} onValueChange={v => setForm(p => ({ ...p, category_id: v === "none" ? "" : v }))}>
              <SelectTrigger><SelectValue placeholder="No category" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none"><span className="text-slate-400">No category linked</span></SelectItem>
                {(() => { const seen = new Set(); return categories?.filter(c => { const n = (c.name || "").trim().toLowerCase(); if (seen.has(n)) return false; seen.add(n); return true; }).map(c => <SelectItem key={c.category_id} value={c.category_id}>{c.name}</SelectItem>); })()}
              </SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs text-slate-500">Behavior</Label>
            <div className="flex gap-2">
              {[{ v: "fixed", l: "Fixed" }, { v: "adjustable", l: "Adjustable" }, { v: "temporary", l: "Temporary" }].map(b => (
                <button key={b.v} type="button" onClick={() => setForm(p => ({ ...p, behavior: b.v }))} className={`flex-1 py-2 px-3 rounded-lg text-sm transition-colors ${form.behavior === b.v ? "bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900" : "bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-400"}`}>{b.l}</button>
              ))}
            </div>
          </div>
          <div><Label className="text-xs text-slate-500">Notes (optional)</Label><Input placeholder="Any details..." value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} /></div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={loading} className="rounded-full bg-slate-900 hover:bg-slate-800 dark:bg-slate-100 dark:text-slate-900" data-testid="save-expense-btn">{loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}{isEdit ? "Save" : "Add"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ================================================================
   MAIN — PlannedExpensesContent
   ================================================================ */
export function PlannedExpensesContent() {
  const { household } = useAuth();
  const [groups, setGroups] = useState([]);
  const [allocations, setAllocations] = useState([]);
  const [summary, setSummary] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [collapsedGroups, setCollapsedGroups] = useState({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAlloc, setEditingAlloc] = useState(null);
  const [groupDialogOpen, setGroupDialogOpen] = useState(false);
  const [newGroupName, setNewGroupName] = useState("");
  const [bills, setBills] = useState([]);
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [showBillDialog, setShowBillDialog] = useState(false);
  const [billForm, setBillForm] = useState({ name: "", amount: "", frequency: "monthly", due_date: "", autopay: false, category_id: "" });
  const [billSaving, setBillSaving] = useState(false);
  const [syncModalOpen, setSyncModalOpen] = useState(false);

  const currency = household?.currency || "NZD";

  const fetchData = useCallback(async () => {
    if (!household?.household_id) { setLoading(false); return; }
    setLoading(true);
    try {
      const [gRes, aRes, sRes, cRes, bRes, calRes] = await Promise.all([
        fetch(`${API}/api/households/${household.household_id}/allocations/groups`, { credentials: "include" }),
        fetch(`${API}/api/households/${household.household_id}/allocations/`, { credentials: "include" }),
        fetch(`${API}/api/households/${household.household_id}/allocations/summary/current`, { credentials: "include" }),
        fetch(`${API}/api/households/${household.household_id}/allocations/categories`, { credentials: "include" }),
        fetch(`${API}/api/households/${household.household_id}/bills`, { credentials: "include" }),
        fetch(`${API}/api/households/${household.household_id}/calendar/financial?days=30`, { credentials: "include" }),
      ]);
      if (gRes.ok) {
        const gData = await gRes.json();
        setGroups(gData.groups || []);
        const cState = {};
        (gData.groups || []).forEach(g => { cState[g.group_id] = g.is_collapsed || false; });
        setCollapsedGroups(prev => ({ ...cState, ...prev }));
      } else {
        console.error(`[Expenses] Groups fetch: ${gRes.status}`);
        if (gRes.status === 401) { toast.error("Session expired — please log in again."); return; }
      }
      if (aRes.ok) setAllocations((await aRes.json()).allocations || []);
      if (sRes.ok) setSummary(await sRes.json());
      if (cRes.ok) setCategories((await cRes.json()).categories || []);
      if (bRes.ok) { const d = await bRes.json(); setBills(Array.isArray(d) ? d : []); }
      if (calRes.ok) { const d = await calRes.json(); setCalendarEvents(d.events || []); }
    } catch (e) { console.error("[Expenses] Network error:", e); toast.error("Could not load expense data."); }
    finally { setLoading(false); }
  }, [household?.household_id]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openAdd = (group) => { setEditingAlloc({ group_id: group?.group_id }); setDialogOpen(true); };
  const openEdit = (alloc) => { setEditingAlloc(alloc); setDialogOpen(true); };

  const handleSave = async (data) => {
    setSaving(true);
    try {
      const isEdit = !!data.allocation_id;
      const url = isEdit ? `${API}/api/households/${household.household_id}/allocations/${data.allocation_id}` : `${API}/api/households/${household.household_id}/allocations/`;
      const res = await fetch(url, { method: isEdit ? "PATCH" : "POST", headers: { "Content-Type": "application/json" }, credentials: "include", body: JSON.stringify({ group_id: data.group_id, name: data.name, amount: data.amount, cycle: data.cycle || "weekly", behavior: data.behavior, category_id: data.category_id, notes: data.notes || null }) });
      if (!res.ok) throw new Error((await res.json()).detail || "Save failed");
      toast.success(isEdit ? "Updated" : "That's in place now");
      setDialogOpen(false); setEditingAlloc(null); fetchData();
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };

  const handleDelete = async (alloc) => {
    if (!window.confirm(`Delete "${alloc.name}"? This cannot be undone.`)) return;
    try { const r = await fetch(`${API}/api/households/${household.household_id}/allocations/${alloc.allocation_id}`, { method: "DELETE", credentials: "include" }); if (!r.ok) throw new Error("Delete failed"); toast.success("Deleted"); fetchData(); } catch (e) { toast.error(e.message); }
  };

  const handlePause = async (alloc) => {
    try { const ep = alloc.status === "paused" ? "resume" : "pause"; const r = await fetch(`${API}/api/households/${household.household_id}/allocations/${alloc.allocation_id}/${ep}`, { method: "POST", credentials: "include" }); if (!r.ok) throw new Error("Failed"); toast.success(alloc.status === "paused" ? "Resumed" : "Paused"); fetchData(); } catch (e) { toast.error(e.message); }
  };

  const handleCreateGroup = async () => {
    if (!newGroupName.trim()) { toast.error("Enter a bucket name"); return; }
    try { const r = await fetch(`${API}/api/households/${household.household_id}/allocations/groups`, { method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include", body: JSON.stringify({ name: newGroupName.trim(), color: "#6366f1" }) }); if (!r.ok) throw new Error("Failed to create bucket"); toast.success("That's in place now"); setGroupDialogOpen(false); setNewGroupName(""); fetchData(); } catch (e) { toast.error(e.message); }
  };

  const handleSaveBill = async () => {
    if (!billForm.name || !billForm.amount || !billForm.due_date) { toast.error("Name, amount, and due date are required"); return; }
    setBillSaving(true);
    try { const r = await fetch(`${API}/api/households/${household.household_id}/bills`, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ ...billForm, amount: parseFloat(billForm.amount) }) }); if (!r.ok) throw new Error("Failed"); toast.success("Bill added — it's now part of your plan"); setBillForm({ name: "", amount: "", frequency: "monthly", due_date: "", autopay: false, category_id: "" }); setShowBillDialog(false); fetchData(); } catch { toast.error("Couldn't add bill"); } finally { setBillSaving(false); }
  };

  /* loading skeleton */
  if (loading) return (
    <div className="p-6 md:p-8 lg:p-10 max-w-4xl mx-auto space-y-4">
      {[1, 2, 3].map(i => <div key={i} className="bg-white dark:bg-slate-900 rounded-2xl h-24 animate-pulse" />)}
    </div>
  );

  const totalAllocated = summary?.total_allocated || allocations.filter(a => a.status === "active").reduce((s, a) => s + (a.amount || 0), 0);
  const incomeAvailable = summary?.income_available || 0;
  const remaining = incomeAvailable - totalAllocated;

  /* cash pressure signal */
  const pressureSignal = (() => {
    const now = new Date();
    const soon = bills.filter(b => { if (!b.next_due) return false; const d = (new Date(b.next_due) - now) / 864e5; return d >= 0 && d <= 5; });
    const upTotal = bills.reduce((s, b) => { if (!b.next_due) return s; const d = (new Date(b.next_due) - now) / 864e5; return d >= 0 && d <= 14 ? s + (b.amount || 0) : s; }, 0);
    if (upTotal > 0 && remaining >= 0 && upTotal > remaining * 4.33) return { type: "pressure", count: soon.length, total: upTotal };
    if (soon.length > 0) return { type: "due_soon", count: soon.length, total: soon.reduce((s, b) => s + (b.amount || 0), 0) };
    if (bills.length > 0) return { type: "clear" };
    return null;
  })();

  /* enrich allocations */
  const billMap = {}; bills.forEach(b => { billMap[b.bill_id] = b; });
  const enriched = allocations.map(a => ({ ...a, _bill: a.bill_id ? billMap[a.bill_id] || null : null }));

  /* deduped categories */
  const dedupCats = (() => { const s = new Set(); return (categories || []).filter(c => { const n = (c.name || "").trim().toLowerCase(); if (s.has(n)) return false; s.add(n); return true; }); })();

  /* ───────── RENDER ───────── */
  return (
    <div className="p-6 md:p-8 lg:p-10 max-w-4xl mx-auto space-y-5" data-testid="planned-expenses-content">
      <ConversationPrompt promptKey="expenses" />

      {/* 1 · Cash Pressure */}
      {pressureSignal && <CashPressureCard signal={pressureSignal} />}

      {/* 2 · Financial Calendar */}
      <FinancialCalendar events={calendarEvents} currency={currency} onSync={() => setSyncModalOpen(true)} />

      {/* 3 · Plan Entry Strip */}
      <PlanEntryStrip household={household} onAddBill={() => setShowBillDialog(true)} />

      {/* 4 · Summary Strip */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white dark:bg-slate-900 rounded-2xl p-5 shadow-sm">
          <p className="text-xs uppercase tracking-wide text-slate-400 mb-1">Income Available</p>
          <p className="text-2xl font-semibold text-slate-900 dark:text-slate-100 tabular-nums">{fmt$(incomeAvailable, currency)}</p>
          <p className="text-xs text-slate-400 mt-0.5">This week</p>
        </div>
        <div className="bg-white dark:bg-slate-900 rounded-2xl p-5 shadow-sm">
          <p className="text-xs uppercase tracking-wide text-slate-400 mb-1">Total Expenses</p>
          <p className="text-2xl font-semibold text-slate-900 dark:text-slate-100 tabular-nums" data-testid="total-expenses">{fmt$(totalAllocated, currency)}</p>
          <p className="text-xs text-slate-400 mt-0.5">{allocations.filter(a => a.status === "active").length} items</p>
        </div>
        <div className={`rounded-2xl p-5 shadow-sm ${remaining < 0 ? "bg-amber-50 dark:bg-amber-900/20" : remaining === 0 ? "bg-emerald-50 dark:bg-emerald-900/20" : "bg-slate-50 dark:bg-slate-800/50"}`}>
          <p className="text-xs uppercase tracking-wide text-slate-400 mb-1">Remaining</p>
          <p className={`text-2xl font-semibold tabular-nums ${remaining < 0 ? "text-amber-600" : remaining === 0 ? "text-emerald-600" : "text-slate-600"}`} data-testid="remaining-budget">{fmt$(remaining, currency)}</p>
          <p className="text-xs text-slate-400 mt-0.5">{remaining < 0 ? "Over-allocated" : remaining === 0 ? "Fully allocated" : "To allocate"}</p>
        </div>
      </div>

      {/* 5 · Bucket actions */}
      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">Expenses by Bucket</h2>
        <div className="flex items-center gap-2">
          <Button onClick={() => setGroupDialogOpen(true)} size="sm" variant="ghost" className="text-xs text-slate-500" data-testid="add-bucket-btn"><Plus className="w-3.5 h-3.5 mr-1" />New bucket</Button>
          {groups.length > 0 && <Button onClick={() => openAdd(groups[0])} size="sm" variant="outline" className="rounded-full" data-testid="add-expense-btn"><Plus className="w-4 h-4 mr-1" />Add an expense</Button>}
        </div>
      </div>

      {/* 5 · Bucket groups */}
      {groups.length === 0 ? (
        <div className="text-center py-16 border-2 border-dashed border-slate-200 dark:border-slate-700 rounded-xl" data-testid="expenses-empty">
          <Wallet className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="text-sm font-medium mb-1">Add the essentials first, then build from there</p>
          <p className="text-xs text-muted-foreground mb-4 max-w-sm mx-auto">Start with what must be covered this cycle. Default buckets will appear automatically.</p>
          <Button onClick={() => openAdd(null)} variant="outline" size="sm">Add an expense</Button>
        </div>
      ) : (
        <div className="space-y-4">
          {groups.map(g => <BucketGroup key={g.group_id} group={g} allocations={enriched.filter(a => a.group_id === g.group_id)} currency={currency} onAdd={openAdd} onEdit={openEdit} onDelete={handleDelete} onPause={handlePause} collapsed={collapsedGroups[g.group_id] || false} onToggle={() => setCollapsedGroups(p => ({ ...p, [g.group_id]: !p[g.group_id] }))} />)}
        </div>
      )}

      {/* Dialogs */}
      <ExpenseDialog open={dialogOpen} onOpenChange={setDialogOpen} allocation={editingAlloc} groups={groups} categories={categories} onSave={handleSave} loading={saving} />

      <Dialog open={groupDialogOpen} onOpenChange={setGroupDialogOpen}>
        <DialogContent className="sm:max-w-sm" aria-describedby="new-bucket-desc">
          <DialogHeader><DialogTitle>New Bucket</DialogTitle></DialogHeader>
          <p id="new-bucket-desc" className="sr-only">Create a custom expense bucket</p>
          <div className="py-4"><Label className="text-xs text-slate-500">Bucket Name</Label><Input placeholder="e.g., Entertainment, Education" value={newGroupName} onChange={e => setNewGroupName(e.target.value)} data-testid="new-bucket-name-input" /></div>
          <DialogFooter><Button variant="ghost" onClick={() => setGroupDialogOpen(false)}>Cancel</Button><Button onClick={handleCreateGroup} className="rounded-full bg-slate-900 hover:bg-slate-800 dark:bg-slate-100 dark:text-slate-900" data-testid="create-bucket-btn">Add</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showBillDialog} onOpenChange={setShowBillDialog}>
        <DialogContent className="sm:max-w-sm" aria-describedby="add-bill-desc">
          <DialogHeader><DialogTitle>Add Bill</DialogTitle></DialogHeader>
          <p id="add-bill-desc" className="sr-only">Add a recurring bill to your plan</p>
          <div className="space-y-3 py-2">
            <div><Label className="text-xs text-muted-foreground">Name</Label><Input placeholder="e.g., Power, Internet" value={billForm.name} onChange={e => setBillForm(f => ({ ...f, name: e.target.value }))} data-testid="bill-name-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs text-muted-foreground">Amount</Label><Input type="number" placeholder="0.00" value={billForm.amount} onChange={e => setBillForm(f => ({ ...f, amount: e.target.value }))} data-testid="bill-amount-input" /></div>
              <div><Label className="text-xs text-muted-foreground">Frequency</Label>
                <Select value={billForm.frequency} onValueChange={v => setBillForm(f => ({ ...f, frequency: v }))}>
                  <SelectTrigger data-testid="bill-frequency-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="weekly">Weekly</SelectItem><SelectItem value="fortnightly">Fortnightly</SelectItem><SelectItem value="monthly">Monthly</SelectItem><SelectItem value="quarterly">Quarterly</SelectItem><SelectItem value="yearly">Yearly</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <div><Label className="text-xs text-muted-foreground">Due date</Label><Input type="date" value={billForm.due_date} onChange={e => setBillForm(f => ({ ...f, due_date: e.target.value }))} data-testid="bill-due-input" /></div>
            <div><Label className="text-xs text-muted-foreground">Category</Label>
              <Select value={billForm.category_id || "none"} onValueChange={v => setBillForm(f => ({ ...f, category_id: v === "none" ? "" : v }))}>
                <SelectTrigger data-testid="bill-category-select"><SelectValue placeholder="No category" /></SelectTrigger>
                <SelectContent><SelectItem value="none"><span className="text-slate-400">No category linked</span></SelectItem>{dedupCats.map(c => <SelectItem key={c.category_id} value={c.category_id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={billForm.autopay} onChange={e => setBillForm(f => ({ ...f, autopay: e.target.checked }))} className="rounded" /><span className="text-sm text-muted-foreground">Autopay enabled</span></label>
          </div>
          <DialogFooter><Button variant="ghost" onClick={() => setShowBillDialog(false)}>Cancel</Button><Button onClick={handleSaveBill} disabled={billSaving} className="rounded-full bg-slate-900 hover:bg-slate-800 dark:bg-slate-100 dark:text-slate-900" data-testid="save-bill-btn">{billSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Add Bill"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Calendar Sync Modal */}
      <CalendarSyncModal open={syncModalOpen} onOpenChange={setSyncModalOpen} household={household} />
    </div>
  );
}
